import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setCurrentUser } from '../../Store/user/userSlice';
import { getCategories } from '../../Store/categories/categoriesSlice';
import { getProducts } from '../../Store/products/productsSlice';
import { getUsers } from '../../Store/users/usersSlice';
import { getOrders, deleteOrder, updateOrderStatus } from '../../Store/orders/ordersSlice';
import CategoriesList from './CategoriesList';
import CategoryForm from './CategoryForm';
import ProductsList from './ProductsList';
import ProductForm from './ProductForm';
import UsersList from './UsersList';
import UserForm from './UserForm';
import OrdersList from './OrdersList';
import OrderForm from './OrderForm';
import PostersList from './PostersList';
import PosterForm from './PosterForm';
import { getPosters, deletePoster } from '../../Store/posters/postersSlice';
import { getReviews, deleteReview } from '../../Store/reviews/reviewsSlice';
import ReviewsList from './ReviewsList';
import ContactsEditor from './ContactsEditor';
import AdminProfile from './AdminProfile';
import DeleteModal from './DeleteModal';
import AdminLogin from './AdminLogin';
import styles from '../../styles/AdminPanel.module.scss';
import LOGO from '../../images/logo.png';

const isAdmin = (user) => user && String(user.role).toUpperCase() === 'ADMIN';

const AdminPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list: categories } = useSelector((state) => state.categories);
  const { list: products } = useSelector((state) => state.products);
  const { list: users } = useSelector((state) => state.users);
  const { list: orders, error: ordersError, isLoading: ordersLoading } = useSelector((state) => state.orders);
  const { list: posters, isLoading: postersLoading, error: postersError } = useSelector((state) => state.posters);
  const { list: reviews, isLoading: reviewsLoading, error: reviewsError } = useSelector((state) => state.reviews);
  const { currentUser, sessionInitialized } = useSelector((state) => state.user);

  // Состояния для навигации
  const [currentPage, setCurrentPage] = useState('categories-list');
  const [editingCategory, setEditingCategory] = useState(null);
  const [defaultProductCategoryId, setDefaultProductCategoryId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editingPoster, setEditingPoster] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', item: null, onConfirm: null });
  const [accordionOpen, setAccordionOpen] = useState({
    categories: true,
    products: false,
    users: false,
    orders: false,
    homepage: false,
    reviews: false,
  });

  useEffect(() => {
    if (!isAdmin(currentUser)) return;
    dispatch(getCategories());
    dispatch(getProducts());
    dispatch(getUsers());
    dispatch(getOrders());
    dispatch(getPosters());
  }, [dispatch, currentUser]);

  // Автоматическое открытие соответствующей секции аккордеона и обновление данных
  useEffect(() => {
    if (!isAdmin(currentUser)) return;
    if (currentPage === 'categories-list' || currentPage === 'category-create') {
      setAccordionOpen(prev => ({ ...prev, categories: true }));
    }
    if (currentPage === 'products-list' || currentPage === 'product-create') {
      setAccordionOpen(prev => ({ ...prev, products: true }));
    }
    if (currentPage === 'users-list') {
      setAccordionOpen(prev => ({ ...prev, users: true }));
      // Обновляем список пользователей при открытии страницы
      dispatch(getUsers());
    }
    if (currentPage === 'orders-list') {
      setAccordionOpen(prev => ({ ...prev, orders: true }));
      // Обновляем список заказов при открытии страницы
      dispatch(getOrders());
    }
    if (currentPage === 'posters-list' || currentPage === 'poster-create' || currentPage === 'poster-edit') {
      setAccordionOpen(prev => ({ ...prev, homepage: true }));
      dispatch(getPosters());
      dispatch(getProducts());
    }
    if (currentPage === 'reviews-list') {
      setAccordionOpen(prev => ({ ...prev, reviews: true }));
      dispatch(getReviews());
    }
    if (currentPage === 'contacts-edit') {
      setAccordionOpen(prev => ({ ...prev, homepage: true }));
    }
  }, [currentPage, dispatch, currentUser]);

  if (!sessionInitialized) {
    return (
      <div className={styles.adminWrapper}>
        <div className={styles.error}>Завантаження сессии…</div>
      </div>
    );
  }

  if (!isAdmin(currentUser)) {
    return (
      <div className={styles.adminWrapper}>
        <AdminLogin />
      </div>
    );
  }

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    dispatch(setCurrentUser(null));
    navigate('/');
  };

  const toggleAccordion = (section) => {
    setAccordionOpen(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Категорії
  const openCategoryEdit = (category) => {
    setEditingCategory(category);
    setCurrentPage('category-create');
    setAccordionOpen(prev => ({ ...prev, categories: true }));
  };

  const openCategoryCreate = () => {
    setEditingCategory(null);
    setCurrentPage('category-create');
    setAccordionOpen(prev => ({ ...prev, categories: true }));
  };

  const handleCategoryDelete = (category) => {
    setDeleteModal({
      show: true,
      type: 'category',
      item: category,
      onConfirm: async () => {
        try {
          const { deleteCategory } = await import('../../Store/categories/categoriesSlice');
          await dispatch(deleteCategory(category.id)).unwrap();
          showMessage('success', 'Категорію успішно видалено!');
          dispatch(getCategories());
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        } catch (error) {
          showMessage('error', 'Помилка при видаленні категорії');
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        }
      }
    });
  };

  const handleCategorySuccess = () => {
    setCurrentPage('categories-list');
    setEditingCategory(null);
  };

  const handleCategoryCancel = () => {
    setCurrentPage('categories-list');
    setEditingCategory(null);
  };

  // Товари
  const openProductEdit = (product) => {
    setEditingProduct(product);
    setCurrentPage('product-create');
    setAccordionOpen(prev => ({ ...prev, products: true }));
  };

  const openProductCreate = (categoryId = null) => {
    setEditingProduct(null);
    setDefaultProductCategoryId(categoryId);
    setCurrentPage('product-create');
    setAccordionOpen(prev => ({ ...prev, products: true }));
  };

  const handleProductDelete = (product) => {
    setDeleteModal({
      show: true,
      type: 'product',
      item: product,
      onConfirm: async () => {
        try {
          const { deleteProduct } = await import('../../Store/products/productsSlice');
          await dispatch(deleteProduct(product.id)).unwrap();
          showMessage('success', 'Товар успішно видалено!');
          dispatch(getProducts());
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        } catch (error) {
          showMessage('error', 'Помилка при видаленні товару');
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        }
      }
    });
  };

  const handleProductSuccess = () => {
    setCurrentPage(defaultProductCategoryId ? 'categories-list' : 'products-list');
    setEditingProduct(null);
    setDefaultProductCategoryId(null);
  };

  const handleProductCancel = () => {
    setCurrentPage(defaultProductCategoryId ? 'categories-list' : 'products-list');
    setEditingProduct(null);
    setDefaultProductCategoryId(null);
  };

  const openProductCreateInCategory = (categoryId) => {
    openProductCreate(categoryId);
  };

  // Користувачі
  const openUserEdit = (user) => {
    setEditingUser(user);
    setCurrentPage('user-edit');
    setAccordionOpen(prev => ({ ...prev, users: true }));
  };

  const handleUserDelete = (user) => {
    setDeleteModal({
      show: true,
      type: 'user',
      item: user,
      onConfirm: async () => {
        try {
          const { deleteUser } = await import('../../Store/users/usersSlice');
          await dispatch(deleteUser(user.id)).unwrap();
          showMessage('success', 'Користувача успішно видалено!');
          dispatch(getUsers());
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        } catch (error) {
          showMessage('error', 'Помилка при видаленні користувача');
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        }
      }
    });
  };

  const handleUserSuccess = () => {
    setCurrentPage('users-list');
    setEditingUser(null);
    dispatch(getUsers());
  };

  const handleUserCancel = () => {
    setCurrentPage('users-list');
    setEditingUser(null);
  };

  // Замовлення
  const openOrderEdit = (order) => {
    setEditingOrder(order);
    setCurrentPage('order-edit');
    setAccordionOpen(prev => ({ ...prev, orders: true }));
  };

  const handleOrderDelete = (order) => {
    setDeleteModal({
      show: true,
      type: 'order',
      item: order,
      onConfirm: async () => {
        try {
          await dispatch(deleteOrder(order.id)).unwrap();
          showMessage('success', 'Замовлення успішно видалено!');
          dispatch(getOrders());
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        } catch (error) {
          showMessage('error', 'Помилка при видаленні замовлення');
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        }
      }
    });
  };

  const handleOrderSuccess = () => {
    setCurrentPage('orders-list');
    setEditingOrder(null);
    dispatch(getOrders());
  };

  const handleOrderCancel = () => {
    setCurrentPage('orders-list');
    setEditingOrder(null);
  };

  const handleOrderStatusChange = async (orderId, payload) => {
    try {
      await dispatch(updateOrderStatus({ id: orderId, ...payload })).unwrap();
      showMessage('success', 'Статус замовлення оновлено');
    } catch (err) {
      showMessage('error', typeof err === 'string' ? err : 'Не вдалося оновити статус замовлення');
      throw err;
    }
  };

  // Постери
  const openPosterEdit = (poster) => {
    setEditingPoster(poster);
    setCurrentPage('poster-edit');
    setAccordionOpen(prev => ({ ...prev, homepage: true }));
  };

  const openPosterCreate = () => {
    setEditingPoster(null);
    setCurrentPage('poster-create');
    setAccordionOpen(prev => ({ ...prev, homepage: true }));
  };

  const handlePosterDelete = (poster) => {
    setDeleteModal({
      show: true,
      type: 'poster',
      item: poster,
      onConfirm: async () => {
        try {
          await dispatch(deletePoster(poster.id)).unwrap();
          showMessage('success', 'Постер успішно видалено!');
          dispatch(getPosters());
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        } catch (error) {
          showMessage('error', 'Помилка при видаленні постера');
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        }
      }
    });
  };

  const handlePosterSuccess = () => {
    setCurrentPage('posters-list');
    setEditingPoster(null);
    dispatch(getPosters());
  };

  const handlePosterCancel = () => {
    setCurrentPage('posters-list');
    setEditingPoster(null);
  };

  const handleReviewDelete = (review) => {
    setDeleteModal({
      show: true,
      type: 'review',
      item: review,
      onConfirm: async () => {
        try {
          await dispatch(deleteReview(review.id)).unwrap();
          showMessage('success', 'Відгук видалено');
          dispatch(getReviews());
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        } catch (error) {
          showMessage('error', 'Помилка при видаленні відгуку');
          setDeleteModal({ show: false, type: '', item: null, onConfirm: null });
        }
      },
    });
  };

  const getPageTitle = () => {
    switch (currentPage) {
      case 'categories-list':
        return 'Список категорій';
      case 'category-create':
        return editingCategory ? 'Редагування категорії' : 'Створення категорії';
      case 'products-list':
        return 'Список товарів';
      case 'product-create':
        return editingProduct ? 'Редагування товару' : 'Створення товару';
      case 'users-list':
        return 'Список користувачів';
      case 'user-edit':
        return editingUser ? 'Редагування користувача' : 'Створення користувача';
      case 'orders-list':
        return 'Список замовлень';
      case 'order-edit':
        return 'Редагування замовлення';
      case 'posters-list':
        return 'Список постерів';
      case 'poster-create':
        return editingPoster ? 'Редагування постера' : 'Створення постера';
      case 'poster-edit':
        return 'Редагування постера';
      case 'reviews-list':
        return 'Відгуки';
      case 'contacts-edit':
        return 'Контакти';
      case 'admin-profile':
        return 'Профіль администратора';
      default:
        return 'Адмін панель';
    }
  };

  return (
    <div className={styles.adminWrapper}>
      {/* Блок 1: Логотип и название */}
      <div className={styles.headerBlock}>
        <div className={styles.logoSection}>
          <img src={LOGO} alt="Logo" className={styles.logo} />
          <h1 className={styles.adminTitle}>Адмін панель</h1>
        </div>
      </div>

       <div className={styles.contentWrapper}>
        {/* Блок 3: Меню */}
        <div className={styles.menuBlock}>
          <div className={styles.menu}>
            {/* Аккордеон: Категорії */}
            <div className={styles.accordionItem}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion('categories')}
              >
                <span>Категорії</span>
                <svg
                  className={`${styles.accordionIcon} ${accordionOpen.categories ? styles.open : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L1 4L2.4 2.6L6 6.2L9.6 2.6L11 4L6 9Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {accordionOpen.categories && (
                <div className={styles.accordionContent}>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'categories-list' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('categories-list');
                      setAccordionOpen(prev => ({ ...prev, categories: true }));
                    }}
                  >
                    Список категорій
                  </button>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'category-create' ? styles.active : ''}`}
                    onClick={openCategoryCreate}
                  >
                    Створити категорію
                  </button>
                </div>
              )}
            </div>

            {/* Аккордеон: Товари */}
            <div className={styles.accordionItem}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion('products')}
              >
                <span>Товари</span>
                <svg
                  className={`${styles.accordionIcon} ${accordionOpen.products ? styles.open : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L1 4L2.4 2.6L6 6.2L9.6 2.6L11 4L6 9Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {accordionOpen.products && (
                <div className={styles.accordionContent}>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'products-list' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('products-list');
                      setAccordionOpen(prev => ({ ...prev, products: true }));
                    }}
                  >
                    Список товарів
                  </button>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'product-create' ? styles.active : ''}`}
                    onClick={openProductCreate}
                  >
                    Створити товар
                  </button>
                </div>
              )}
            </div>

            {/* Аккордеон: Відгуки */}
            <div className={styles.accordionItem}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion('reviews')}
              >
                <span>Відгуки</span>
                <svg
                  className={`${styles.accordionIcon} ${accordionOpen.reviews ? styles.open : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L1 4L2.4 2.6L6 6.2L9.6 2.6L11 4L6 9Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {accordionOpen.reviews && (
                <div className={styles.accordionContent}>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'reviews-list' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('reviews-list');
                      setAccordionOpen(prev => ({ ...prev, reviews: true }));
                    }}
                  >
                    Список відгуків
                  </button>
                </div>
              )}
            </div>

            {/* Аккордеон: Користувачі */}
            <div className={styles.accordionItem}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion('users')}
              >
                <span>Користувачі</span>
                <svg
                  className={`${styles.accordionIcon} ${accordionOpen.users ? styles.open : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L1 4L2.4 2.6L6 6.2L9.6 2.6L11 4L6 9Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {accordionOpen.users && (
                <div className={styles.accordionContent}>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'users-list' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('users-list');
                      setAccordionOpen(prev => ({ ...prev, users: true }));
                    }}
                  >
                    Список користувачів
                  </button>
                </div>
              )}
            </div>

            {/* Аккордеон: Замовлення */}
            <div className={styles.accordionItem}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion('orders')}
              >
                <span>Замовлення</span>
                <svg
                  className={`${styles.accordionIcon} ${accordionOpen.orders ? styles.open : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L1 4L2.4 2.6L6 6.2L9.6 2.6L11 4L6 9Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {accordionOpen.orders && (
                <div className={styles.accordionContent}>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'orders-list' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('orders-list');
                      setAccordionOpen(prev => ({ ...prev, orders: true }));
                    }}
                  >
                    Список замовлень
                  </button>
                </div>
              )}
            </div>

            {/* Аккордеон: Головна сторінка */}
            <div className={styles.accordionItem}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleAccordion('homepage')}
              >
                <span>Головна сторінка</span>
                <svg
                  className={`${styles.accordionIcon} ${accordionOpen.homepage ? styles.open : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L1 4L2.4 2.6L6 6.2L9.6 2.6L11 4L6 9Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {accordionOpen.homepage && (
                <div className={styles.accordionContent}>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'posters-list' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('posters-list');
                      setAccordionOpen(prev => ({ ...prev, homepage: true }));
                    }}
                  >
                    Список постерів
                  </button>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'poster-create' ? styles.active : ''}`}
                    onClick={openPosterCreate}
                  >
                    Створити постер
                  </button>
                  <button
                    className={`${styles.menuButton} ${currentPage === 'contacts-edit' ? styles.active : ''}`}
                    onClick={() => {
                      setCurrentPage('contacts-edit');
                      setAccordionOpen(prev => ({ ...prev, homepage: true }));
                    }}
                  >
                    Контакти
                  </button>
                </div>
              )}
            </div>

            {/* Профіль администратора */}
            <button
              className={`${styles.menuButton} ${currentPage === 'admin-profile' ? styles.active : ''}`}
              onClick={() => setCurrentPage('admin-profile')}
              style={{ marginTop: '10px' }}
            >
              Профіль
            </button>

            <button
              className={styles.logoutButton}
              onClick={handleLogout}
            >
              Вихід
            </button>
          </div>
        </div>

    <div className={styles.contentWrapperBox}>


        {/* Блок 2: Назва страницы */}
      {currentPage !== 'product-create' && currentPage !== 'products-list' && currentPage !== 'categories-list' && currentPage !== 'category-create' && currentPage !== 'orders-list' && currentPage !== 'order-edit' && currentPage !== 'posters-list' && currentPage !== 'poster-create' && currentPage !== 'poster-edit' && currentPage !== 'reviews-list' && currentPage !== 'contacts-edit' && (
        <div className={styles.pageTitleBlock}>
          <h2 className={styles.pageTitle}>{getPageTitle()}</h2>
        </div>
      )}

        {/* Блок 4: Контент */}
        <div className={styles.contentBlock}>
          {message.text && (
            <div className={`${styles.message} ${styles[message.type]}`}>
              {message.text}
            </div>
          )}

          {/* Страница: Список категорій */}
          {currentPage === 'categories-list' && (
            <div className={styles.listPage}>
              <CategoriesList
                categories={categories}
                products={products}
                onEdit={openCategoryEdit}
                onDelete={handleCategoryDelete}
                onCreate={openCategoryCreate}
                onEditProduct={openProductEdit}
                onDeleteProduct={handleProductDelete}
                onCreateProduct={openProductCreateInCategory}
              />
            </div>
          )}

          {/* Страница: Создание/Редагування категорії */}
          {currentPage === 'category-create' && (
            <div className={styles.categoryFormPage}>
              <CategoryForm
                key={editingCategory?.id ?? 'new'}
                category={editingCategory}
                categories={categories}
                onCancel={handleCategoryCancel}
                onSuccess={handleCategorySuccess}
                onDelete={handleCategoryDelete}
                showMessage={showMessage}
              />
            </div>
          )}

          {/* Страница: Список товарів */}
          {currentPage === 'products-list' && (
            <div className={styles.listPage}>
              <ProductsList
                products={products}
                onEdit={openProductEdit}
                onDelete={handleProductDelete}
                onCreate={openProductCreate}
              />
            </div>
          )}

          {/* Страница: Создание/Редагування товару */}
          {currentPage === 'product-create' && (
            <div className={styles.productFormPage}>
              <ProductForm
                key={editingProduct?.id ?? `new-${defaultProductCategoryId ?? 'none'}`}
                product={editingProduct}
                initialCategoryId={defaultProductCategoryId}
                categories={categories}
                products={products}
                onCancel={handleProductCancel}
                onSuccess={handleProductSuccess}
                onSaved={setEditingProduct}
                showMessage={showMessage}
              />
            </div>
          )}

          {/* Страница: Список користувачів */}
          {currentPage === 'users-list' && (
            <div className={styles.listPage}>
              <div className={styles.listHeader}>
                <h3>Список користувачів ({users.length})</h3>
                <button
                  className={styles.refreshButton}
                  onClick={() => dispatch(getUsers())}
                  title="Оновити список"
                >
                  Оновити
                </button>
              </div>
              <UsersList 
                users={users} 
                onEdit={openUserEdit}
                onDelete={handleUserDelete}
              />
            </div>
          )}

          {/* Страница: Редагування користувача */}
          {currentPage === 'user-edit' && (
            <UserForm
              user={editingUser}
              onCancel={handleUserCancel}
              onSuccess={handleUserSuccess}
              showMessage={showMessage}
            />
          )}

          {/* Страница: Список замовлень */}
          {currentPage === 'orders-list' && (
            <div className={styles.listPage}>
              <OrdersList
                orders={orders}
                loading={ordersLoading}
                error={ordersError}
                onEdit={openOrderEdit}
                onDelete={handleOrderDelete}
                onStatusChange={handleOrderStatusChange}
                onRefresh={() => dispatch(getOrders())}
              />
            </div>
          )}

          {/* Страница: Редагування замовлення */}
          {currentPage === 'order-edit' && (
            <OrderForm
              order={editingOrder}
              onCancel={handleOrderCancel}
              onSuccess={handleOrderSuccess}
              onDelete={handleOrderDelete}
              showMessage={showMessage}
            />
          )}

          {/* Страница: Список постерів */}
          {currentPage === 'posters-list' && (
            <div className={styles.listPage}>
              <PostersList
                posters={posters || []}
                products={products}
                loading={postersLoading}
                error={postersError}
                onEdit={openPosterEdit}
                onDelete={handlePosterDelete}
                onCreate={openPosterCreate}
                onRefresh={() => dispatch(getPosters())}
                showMessage={showMessage}
              />
            </div>
          )}

          {(currentPage === 'poster-create' || currentPage === 'poster-edit') && (
            <PosterForm
              poster={editingPoster}
              onCancel={handlePosterCancel}
              onSuccess={handlePosterSuccess}
              onDelete={handlePosterDelete}
              showMessage={showMessage}
            />
          )}

          {currentPage === 'reviews-list' && (
            <div className={styles.listPage}>
              <ReviewsList
                reviews={reviews}
                loading={reviewsLoading}
                error={reviewsError}
                onRefresh={() => dispatch(getReviews())}
                onDelete={handleReviewDelete}
                showMessage={showMessage}
              />
            </div>
          )}

          {currentPage === 'contacts-edit' && (
            <ContactsEditor showMessage={showMessage} />
          )}

          {/* Страница: Профіль администратора */}
          {currentPage === 'admin-profile' && (
            <AdminProfile showMessage={showMessage} />
          )}
        </div>
        </div>
      </div>

      {/* Модальное окно подтверждения удаления */}
      <DeleteModal
        show={deleteModal.show}
        type={deleteModal.type}
        item={deleteModal.item}
        onConfirm={deleteModal.onConfirm}
        onCancel={() => setDeleteModal({ show: false, type: '', item: null, onConfirm: null })}
      />
    </div>
  );
};

export default AdminPanel;
